/**
 * Web component: Campaign teaser (campaign-teaser)
 * -----------------------------------------------------------------------------
 * Campaign Teaser.
 *
 */
import classes from '~/config/classes';
import './campaign-teaser.scss';

import { vueReady } from '~/core/runners';

import { dateTime } from '~/helpers/general';

import {
  handleCopy,
  handleTrackingCampaign,
  updateTeaser,
} from '~/helpers/teasers';

/**
 * DOM selectors.
 */
const selectors = {
  campaignContainer: '[js-campaign-teaser="content"]',
  contentCounting: '[js-campaign-teaser="contentCounting"]',
  contentDefault: '[js-campaign-teaser="contentDefault"]',
  copyButton: '[js-campaign-teaser="copyButton"]',
  tooltip: '[js-campaign-teaser="tooltip"]',
  promoText: '[js-campaign-teaser="promoText"]',
};

/**
 * Create new <campaign-teaser> custom element.
 */
class CampaignTeaser extends HTMLElement {
  data(component) {
    const campaignContainer = component.querySelector(
      selectors.campaignContainer,
    );
    if (campaignContainer) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleTrackingCampaign(
              campaignContainer,
              'Campaign Teaser',
              'view_promotion',
            );
            observer.disconnect();
          }
        });
      });

      observer.observe(campaignContainer);
    }

    return {
      selectors: {
        campaignContainer,
        copyButton: component.querySelector(selectors.copyButton),
        promoText: component.querySelector(selectors.promoText),
      },
    };
  }

  constructor() {
    super();
    this.$data = this.data(this);
    this.teaserUpdated = false;

    // for initial load, read campaign state from local storage and update
    // teaser if necessary
    const { copyButton } = this.$data.selectors;
    if (copyButton) {
      const discountCampaigns = JSON.parse(
        localStorage.getItem('discount-campaigns'),
      );

      updateTeaser.call(this, discountCampaigns);

      // listen to campaign changes, update teaser content
      cnvs.EventBus.listen('discount-campaign', (campaigns) => {
        updateTeaser.call(this, campaigns);
      });
    }
  }

  /**
   * Component lifecycle - mounted.
   */
  connectedCallback() {
    this.$data = Object.keys(this.$data).length ? this.$data : this.data(this);

    const { copyButton } = this.$data.selectors;

    if (copyButton) {
      copyButton.addEventListener('click', (event) =>
        handleCopy(event, selectors.tooltip),
      );
    }

    let result = dateTime(
      this.dataset.countdownStart,
      this.dataset.countdownEnd,
    );

    if (result) {
      const contentCounting = this.querySelector(selectors.contentCounting);
      const contentDefault = this.querySelector(selectors.contentDefault);

      const countdownInterval = setInterval(() => {
        result = dateTime(
          this.dataset.countdownStart,
          this.dataset.countdownEnd,
        );
        if (!result) {
          contentCounting.classList.add(classes.hidden);
          contentDefault.classList.remove(classes.hidden);
          this.classList.remove(classes.active);
          clearInterval(countdownInterval);
          cnvs.EventBus.emit('campaign:countdown-finished');
        }
      }, 1000);
    }
  }
}

/**
 * Define web component on Vue initialisation.
 */
vueReady(() => {
  if (customElements.get('campaign-teaser')) {
    return;
  }

  customElements.define('campaign-teaser', CampaignTeaser);
});
